import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import FillableFormFiller from "@/components/FillableFormFiller";
import IdentityDocsStep from "@/components/IdentityDocsStep";
import { SignaturePad } from "@/pages/OrientationPage";
import { ShieldCheck, Loader2, CheckCircle, FileText, PenTool, Plus, Trash2, Lock, IdCard } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyAddress = (current) => ({ street: "", city: "", state: "WI", zip: "", from_date: "", to_date: "", current });

export default function BackgroundCheckPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    legal_first_name: "", legal_middle_name: "", legal_last_name: "", other_names: "",
    dob: "", ssn: "", phone: "", email: "", dl_number: "", dl_state: "WI",
  });
  const [addresses, setAddresses] = useState([emptyAddress(true)]);
  const [disclosureValues, setDisclosureValues] = useState(null);
  const [filling, setFilling] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signature, setSignature] = useState("");
  const [consent, setConsent] = useState(false);
  const [idStatus, setIdStatus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/background-check/${token}`)
      .then((r) => {
        setData(r.data);
        const parts = (r.data.candidate_name || "").split(" ");
        setForm((f) => ({ ...f, legal_first_name: parts[0] || "", legal_last_name: parts.slice(1).join(" "), email: r.data.candidate_email || "" }));
        setSignerName(r.data.candidate_name || "");
      })
      .catch((err) => setError(err.response?.data?.detail || "This link is not valid"))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setAddr = (i, k, v) => setAddresses((a) => a.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const disclosure = data?.disclosure;
  const disclosureFillable = !!disclosure?.fillable_form?.enabled && (disclosure.fillable_form.fields || []).length > 0;
  const requiresFill = disclosureFillable && !disclosureValues;

  const submit = async () => {
    if (!form.legal_first_name || !form.legal_last_name || !form.dob || !form.ssn) return toast.error("Please complete your legal name, date of birth and SSN");
    if (form.ssn.replace(/\D/g, "").length !== 9) return toast.error("SSN must be 9 digits");
    const cur = addresses.find((a) => a.current);
    if (!cur || !cur.street || !cur.city || !cur.zip) return toast.error("Please complete your current address");
    if (requiresFill) return toast.error("Please fill out the disclosure form first");
    if (idStatus?.missing_required?.length) return toast.error(`Please upload a photo of: ${idStatus.missing_required.join(", ")}`);
    if (!signerName.trim()) return toast.error("Please type your full name");
    if (!signature) return toast.error("Please draw your signature");
    if (!consent) return toast.error("Please check the authorization box");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/background-check/${token}/submit`, {
        ...form,
        addresses,
        disclosure_values: disclosureValues || undefined,
        signer_name: signerName.trim(),
        signature_data_url: signature,
        consent: true,
      });
      setDone(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="max-w-md w-full"><CardContent className="p-8 text-center space-y-2">
        <Lock className="w-10 h-10 mx-auto text-slate-400" />
        <p className="font-medium text-slate-900">Link unavailable</p>
        <p className="text-sm text-slate-500" data-testid="bg-public-error">{error}</p>
      </CardContent></Card>
    </div>
  );

  const agency = data.agency || {};
  const hasIdStep = !idStatus || idStatus.documents.length > 0;
  if (done || data.submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="max-w-md w-full"><CardContent className="p-8 text-center space-y-3" data-testid="bg-public-done">
        <CheckCircle className="w-12 h-12 mx-auto text-emerald-600" />
        <h1 className="text-xl font-semibold text-slate-900">Authorization received</h1>
        <p className="text-sm text-slate-600">{done?.message || `Thank you — ${agency.name || "the agency"} will contact you once your background check is complete.`}</p>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="bg-public-page">
        <div className="flex items-center gap-4">
          {agency.logo_data_url ? <img src={agency.logo_data_url} alt="" className="h-12 object-contain" /> : <ShieldCheck className="w-10 h-10 text-teal-600" />}
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Background Check Authorization</h1>
            <p className="text-sm text-slate-500">{agency.name} · Your information is encrypted and used only to complete your pre-employment background check.</p>
          </div>
        </div>

        <Card><CardContent className="p-6 space-y-4">
          <h2 className="font-semibold text-slate-900">1. Your legal identity</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Legal first name *</Label><Input value={form.legal_first_name} onChange={set("legal_first_name")} data-testid="bg-first-name" /></div>
            <div><Label>Middle name</Label><Input value={form.legal_middle_name} onChange={set("legal_middle_name")} data-testid="bg-middle-name" /></div>
            <div><Label>Legal last name *</Label><Input value={form.legal_last_name} onChange={set("legal_last_name")} data-testid="bg-last-name" /></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Other names used (maiden, alias)</Label><Input value={form.other_names} onChange={set("other_names")} data-testid="bg-other-names" /></div>
            <div><Label>Date of birth *</Label><Input type="date" value={form.dob} onChange={set("dob")} data-testid="bg-dob" /></div>
            <div><Label>Social Security Number *</Label><Input inputMode="numeric" placeholder="###-##-####" value={form.ssn} onChange={set("ssn")} data-testid="bg-ssn" /></div>
          </div>
          <div className="grid sm:grid-cols-4 gap-3">
            <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="bg-phone" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="bg-email" /></div>
            <div><Label>Driver's license #</Label><Input value={form.dl_number} onChange={set("dl_number")} data-testid="bg-dl" /></div>
            <div><Label>DL state</Label><Input value={form.dl_state} onChange={set("dl_state")} maxLength={2} data-testid="bg-dl-state" /></div>
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">2. Addresses (last 7 years)</h2>
            <Button size="sm" variant="outline" onClick={() => setAddresses((a) => [...a, emptyAddress(false)])} className="gap-1" data-testid="bg-add-address"><Plus className="w-3.5 h-3.5" /> Previous address</Button>
          </div>
          {addresses.map((a, i) => (
            <div key={i} className="grid sm:grid-cols-[2fr_1fr_60px_90px_auto] gap-2 items-end p-3 rounded-lg bg-slate-50" data-testid={`bg-address-${i}`}>
              <div><Label className="text-xs">{a.current ? "Current street address *" : "Street"}</Label><Input value={a.street} onChange={(e) => setAddr(i, "street", e.target.value)} data-testid={`bg-address-street-${i}`} /></div>
              <div><Label className="text-xs">City</Label><Input value={a.city} onChange={(e) => setAddr(i, "city", e.target.value)} data-testid={`bg-address-city-${i}`} /></div>
              <div><Label className="text-xs">State</Label><Input value={a.state} onChange={(e) => setAddr(i, "state", e.target.value)} maxLength={2} /></div>
              <div><Label className="text-xs">ZIP</Label><Input value={a.zip} onChange={(e) => setAddr(i, "zip", e.target.value)} data-testid={`bg-address-zip-${i}`} /></div>
              {a.current ? <span className="text-xs text-emerald-700 pb-2">Current</span> : (
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setAddresses((list) => list.filter((_, idx) => idx !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>
              )}
              {!a.current && (
                <div className="sm:col-span-5 grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">From</Label><Input type="month" value={a.from_date} onChange={(e) => setAddr(i, "from_date", e.target.value)} /></div>
                  <div><Label className="text-xs">To</Label><Input type="month" value={a.to_date} onChange={(e) => setAddr(i, "to_date", e.target.value)} /></div>
                </div>
              )}
            </div>
          ))}
        </CardContent></Card>

        <Card className={hasIdStep ? "" : "hidden"}><CardContent className="p-6 space-y-4" data-testid="bg-identity-card">
          <div className="flex items-center gap-2">
            <IdCard className="w-5 h-5 text-teal-600" />
            <h2 className="font-semibold text-slate-900">3. Photos of your ID documents</h2>
          </div>
          <IdentityDocsStep kind="background-check" token={token} onStatus={setIdStatus} />
        </CardContent></Card>

        <Card><CardContent className="p-6 space-y-4">
          <h2 className="font-semibold text-slate-900">{hasIdStep ? 4 : 3}. Disclosure &amp; authorization</h2>
          {disclosure && (
            <div className="flex flex-wrap items-center gap-3">
              <a href={`${BACKEND_URL}${disclosure.document_url}`} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="bg-disclosure-link">
                <FileText className="w-3.5 h-3.5" /> Read: {disclosure.title}
              </a>
              {disclosureFillable && (
                <Button size="sm" variant={disclosureValues ? "outline" : "default"} onClick={() => setFilling(true)} className={`gap-1 ${disclosureValues ? "border-emerald-300 text-emerald-700" : "bg-purple-600 hover:bg-purple-700"}`} data-testid="bg-fill-disclosure">
                  {disclosureValues ? <CheckCircle className="w-3.5 h-3.5" /> : <PenTool className="w-3.5 h-3.5" />}
                  {disclosureValues ? "Form filled — edit" : "Fill out the disclosure form"}
                </Button>
              )}
            </div>
          )}
          <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
            I authorize {agency.name || "the agency"} and its designated screening vendor to obtain consumer reports and criminal history information about me for employment purposes, including checks through state and federal repositories and registries as required for caregivers. I certify the information above is true and complete.
          </p>
          <label className="flex items-start gap-2 text-sm text-slate-800">
            <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="bg-consent" className="mt-0.5" />
            I have read the disclosure and authorize the background check.
          </label>
          <div>
            <Label>Type your full legal name *</Label>
            <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} data-testid="bg-signer-name" />
          </div>
          <div>
            <Label>Draw your signature *</Label>
            <SignaturePad onDataUrl={setSignature} />
          </div>
          <Button onClick={submit} disabled={submitting} className="w-full gap-2 bg-teal-600 hover:bg-teal-700" data-testid="bg-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Submit authorization
          </Button>
        </CardContent></Card>

        {filling && disclosure && (
          <FillableFormFiller
            open
            onClose={() => setFilling(false)}
            form={disclosure}
            pdfEndpoint={`${BACKEND_URL}${disclosure.document_url}`}
            submitLabel="Save answers"
            onSubmitValues={async (values) => { setDisclosureValues(values); toast.success("Answers saved — finish signing below"); }}
          />
        )}
      </div>
    </div>
  );
}
